import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react'
import { graphql } from '@apollo/client/react/hoc'
import { NetworkStatus, useQuery } from '@apollo/client'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Badge, Button } from 'reactstrap'
import moment from 'moment'
import { AllTasksQuery, BoardQuery } from './Queries'
import { UpdateTaskMutation } from './Mutations'
import TaskDetailsSidePanel from './TaskDetailsSidePanel'
import withApolloClient from '../withApolloClient'
import RepAvatar from '../Material/RepAvatar'
import './TaskKanbanView.css'

const KANBAN_COLUMNS = [
    { key: 'OPEN', label: 'Open', mutationValue: 'OPEN' },
    { key: 'IN_PROGRESS', label: 'In Progress', mutationValue: 'IN_PROGRESS' },
    { key: 'DONE', label: 'Done', mutationValue: 'DONE' },
    { key: 'INCOMPLETE', label: 'Incomplete', mutationValue: 'INCOMPLETE' },
]

const PAGE_SIZE = 30
const EMPTY_ENUM_GROUP_KEY = '__EMPTY_ENUM_GROUP__'

const getStatusColumnKey = (status) => status?.toUpperCase().replace(/ /g, '_') || 'OPEN'

const getComparableTaskSignature = (task) => {
    if (!task) return null

    return JSON.stringify({
        id: task.id,
        title: task.title,
        status: task.status,
        dueDate: task.dueDate,
        commentsCount: task.commentsCount,
        dateModified: task.dateModified,
        assignedToIds: task.assignedTo?.edges?.map(({ node }) => node?.id).filter(Boolean) || [],
        columnValues: task.taskcolumnvalueSet?.edges?.map(({ node }) => ({
            columnId: node?.column?.id,
            value: node?.value,
            valueString: node?.valueString,
        })) || [],
    })
}

const getDueDateConfig = (dueDate) => {
    if (!dueDate) return { label: "", isPastDue: false, iconClassName: "" }

    const today = moment().startOf("day")
    const due = moment(dueDate).startOf("day")
    const dayDiff = due.diff(today, "days")

    if (dayDiff === 0) {
        return { label: "Today", isPastDue: false, iconClassName: "fa fa-circle" }
    }
    if (dayDiff > 0) {
        return {
            label: `${dayDiff} Day${dayDiff === 1 ? "" : "s"} Left`,
            isPastDue: false,
            iconClassName: dayDiff < 7 ? "fa fa-circle-o text-muted" : "fa fa-clock-o text-muted",
        }
    }
    if (dayDiff === -1) {
        return { label: "Due Yesterday", isPastDue: true, iconClassName: "fa fa-exclamation-circle text-danger" }
    }

    const overdueDays = Math.abs(dayDiff)
    return {
        label: `${overdueDays} Day${overdueDays === 1 ? "" : "s"} Overdue`,
        isPastDue: true,
        iconClassName: "fa fa-exclamation-circle text-danger",
    }
}

function KanbanColumn({
    column,
    boardIds,
    assignedToIds,
    searchTerm,
    isArchived,
    incidentId,
    orderBy,
    dueDateGte,
    dueDateLte,
    dueDateIsNull,
    localOverrides,
    taskRegistry,
    mutationLoadingMap,
    onTasksLoaded,
    dragOverColumn,
    onDragOverColumn,
    onDrop,
    draggedTask,
    onDragStart,
    onDragEnd,
    onCardClick,
    onQuickUpdate,
    onStartEditTitle,
    editingTaskId,
    editTaskTitle,
    setEditTaskTitle,
    saveTaskTitle,
    cardMenuTaskId,
    setCardMenuTaskId,
    menuRef,
    dragJustEndedRef,
    enumColumns,
}) {
    const { data, loading, fetchMore, networkStatus } = useQuery(AllTasksQuery, {
        variables: {
            boardIds,
            assignedToIds,
            incidentId,
            searchTerm,
            isArchived,
            orderBy: ["-dateModified", ...orderBy],
            dueDateGte,
            dueDateLte,
            dueDateIsNull,
            statuses: [column.mutationValue],
            first: PAGE_SIZE,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
    })

    const queryTasks = useMemo(
        () => data?.allTasks?.edges?.map(({ node }) => node) || [],
        [data]
    )
    const pageInfo = data?.allTasks?.pageInfo
    const isInitialLoading = loading && !data?.allTasks
    const isFetchMoreLoading = networkStatus === NetworkStatus.fetchMore
    const isMutationLoading = !!mutationLoadingMap[column.key]
    const showColumnLoader = isInitialLoading || isFetchMoreLoading || isMutationLoading

    useEffect(() => {
        onTasksLoaded(column.key, queryTasks)
    }, [column.key, queryTasks, onTasksLoaded])

    const overriddenIntoColumn = useMemo(() => {
        const tasks = Object.entries(localOverrides)
            .filter(([, targetColumnKey]) => targetColumnKey === column.key)
            .map(([taskId]) => taskRegistry[taskId])
            .filter(Boolean)

        const existingIds = new Set(queryTasks.map((task) => task.id))
        return tasks.filter((task) => !existingIds.has(task.id))
    }, [column.key, localOverrides, queryTasks, taskRegistry])

    const visibleTasks = useMemo(() => {
        const filteredQueryTasks = queryTasks.filter((task) => {
            const overrideColumn = localOverrides[task.id]
            if (overrideColumn) return overrideColumn === column.key
            return getStatusColumnKey(task.status) === column.key
        })

        return [...overriddenIntoColumn, ...filteredQueryTasks]
    }, [column.key, localOverrides, overriddenIntoColumn, queryTasks])

    const loadMore = () => {
        if (!pageInfo?.hasNextPage) return
        fetchMore({
            variables: {
                after: pageInfo.endCursor,
                first: PAGE_SIZE,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult?.allTasks) return previousResult
                return {
                    allTasks: {
                        ...fetchMoreResult.allTasks,
                        edges: [
                            ...(previousResult?.allTasks?.edges || []),
                            ...(fetchMoreResult?.allTasks?.edges || []),
                        ],
                    },
                }
            },
        })
    }

    return (
        <div
            className={`kanban-column${dragOverColumn === column.key ? ' drag-over' : ''}`}
            onDragOver={(e) => {
                e.preventDefault()
                if (draggedTask) onDragOverColumn(column.key)
            }}
            onDragLeave={() => {
                if (dragOverColumn === column.key) onDragOverColumn(null)
            }}
            onDrop={() => onDrop(column.key)}
        >
            <div className="kanban-column-header">
                <span className={`status-label status-${column.key.toLowerCase()}`}>{column.label}</span>
                <Badge color="secondary" className="ml-2">{pageInfo?.length || 0}</Badge>
            </div>
            {showColumnLoader && (
                <div className="kanban-column-loading-bar">
                    <div className="kanban-loading-bar-fill" />
                </div>
            )}
            <div className="kanban-column-body">
                {visibleTasks.length === 0 && !showColumnLoader && (
                    <div className="kanban-empty">No tasks</div>
                )}
                {visibleTasks.map((task) => {
                    const dueDateConfig = getDueDateConfig(task.dueDate)
                    return (
                        <div
                            key={task.id}
                            className={`kanban-card${draggedTask?.id === task.id ? ' dragging' : ''}`}
                            draggable={editingTaskId !== task.id}
                            onDragStart={() => {
                                if (editingTaskId === task.id) return
                                dragJustEndedRef.current = false
                                onDragStart(task)
                            }}
                            onDragEnd={() => {
                                dragJustEndedRef.current = true
                                setTimeout(() => { dragJustEndedRef.current = false }, 200)
                                onDragEnd()
                            }}
                            onClick={() => {
                                if (dragJustEndedRef.current || editingTaskId === task.id) return
                                onCardClick(task.id)
                            }}
                        >
                            <div className="kanban-card-top">
                                {editingTaskId === task.id ? (
                                    <input
                                        className="kanban-title-input"
                                        value={editTaskTitle}
                                        autoFocus
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => setEditTaskTitle(e.target.value)}
                                        onBlur={() => saveTaskTitle(task.id)}
                                        onKeyDown={e => {
                                            e.stopPropagation()
                                            if (e.key === 'Enter') saveTaskTitle(task.id)
                                            if (e.key === 'Escape') onStartEditTitle(null, '')
                                        }}
                                    />
                                ) : (
                                    <div
                                        className="kanban-card-title"
                                        onClick={e => {
                                            if (task.isAutomated) return
                                            e.stopPropagation()
                                            onStartEditTitle(task.id, task.title)
                                        }}
                                        title={task.isAutomated ? "Automated task titles cannot be edited" : "Click to edit title"}
                                    >
                                        {task.title}
                                    </div>
                                )}
                                <div
                                    className="kanban-card-menu-btn"
                                    ref={cardMenuTaskId === task.id ? menuRef : null}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <span className="kanban-card-menu-trigger" onClick={() => setCardMenuTaskId(cardMenuTaskId === task.id ? null : task.id)}>
                                        <i className="fa fa-ellipsis-v" />
                                    </span>
                                    {cardMenuTaskId === task.id && (
                                        <div className="kanban-card-menu-dropdown">
                                            <div className="kanban-card-menu-section-label">Status</div>
                                            {KANBAN_COLUMNS.map((statusColumn) => (
                                                <div
                                                    key={statusColumn.key}
                                                    className={`kanban-card-menu-option${task.status?.toUpperCase() === statusColumn.key ? ' active' : ''}${task.isAutomated && statusColumn.key === 'DONE' ? ' disabled' : ''}`}
                                                    onClick={() => {
                                                        if (task.isAutomated && statusColumn.key === 'DONE') return
                                                        onQuickUpdate(task.id, { status: statusColumn.mutationValue })
                                                        setCardMenuTaskId(null)
                                                    }}
                                                >
                                                    {statusColumn.label}
                                                </div>
                                            ))}
                                            {enumColumns.map((enumCol) => (
                                                <Fragment key={enumCol.pk}>
                                                    <div className="kanban-card-menu-divider" />
                                                    <div className="kanban-card-menu-section-label">{enumCol.title}</div>
                                                    {(enumCol.allowedOptions || []).map((opt) => {
                                                        const currentVal = task.taskcolumnvalueSet?.edges?.find(
                                                            ({ node }) => String(node.column.pk) === String(enumCol.pk)
                                                        )?.node?.valueString
                                                        return (
                                                            <div
                                                                key={opt}
                                                                className={`kanban-card-menu-option${currentVal === opt ? ' active' : ''}`}
                                                                onClick={() => {
                                                                    onQuickUpdate(task.id, { columnValues: [{ columnId: enumCol.id, value: opt }] })
                                                                    setCardMenuTaskId(null)
                                                                }}
                                                            >
                                                                {opt}
                                                            </div>
                                                        )
                                                    })}
                                                </Fragment>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="kanban-card-bottom">
                                <div className="kanban-card-footer">
                                    {task.isAutomated && (
                                        <span className="kanban-automated" title="Automated task">
                                            <i className="fa fa-cogs task-automated-icon" aria-hidden="true" />
                                        </span>
                                    )}
                                    {task.dueDate && (
                                        <span
                                            className={`kanban-due-date${dueDateConfig.isPastDue ? ' text-danger' : ''}`}
                                            title={dueDateConfig.label}
                                            style={task.status === "DONE" ? { textDecoration: "line-through" } : {}}
                                        >
                                            <i className={dueDateConfig.iconClassName} aria-hidden="true" />{' '}
                                            {moment(task.dueDate).format('MMM D, YYYY')}
                                        </span>
                                    )}
                                    {task.commentsCount > 0 && (
                                        <span className="kanban-comments">
                                            <i className="fa fa-comments" aria-hidden="true" /> {task.commentsCount}
                                        </span>
                                    )}
                                </div>
                                {task.assignedTo?.edges?.length > 0 && (
                                    <div className="kanban-avatars-stack" onClick={e => e.stopPropagation()}>
                                        {task.assignedTo.edges.slice(0, 3).map(({ node: assignee }, idx) => (
                                            <RepAvatar key={idx} rep={assignee} keepClose />
                                        ))}
                                        {task.assignedTo.edges.length > 3 && (
                                            <span className="kanban-avatar-overflow">+{task.assignedTo.edges.length - 3}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className="kanban-column-footer">
                <span className="kanban-column-footer__count">
                    Showing {visibleTasks.length} of {pageInfo?.length || 0}
                </span>
                {pageInfo?.hasNextPage && (
                    <Button color="link" className="kanban-column-footer__more" onClick={loadMore}>
                        Show More
                    </Button>
                )}
            </div>
        </div>
    )
}

function LegacyKanbanView({
    allTasks,
    loading,
    computedColumns,
    localOverrides,
    groupBy,
    draggedTaskId,
    dragJustEndedRef,
    handleDrop,
    setDraggedTaskId,
    setCardMenuTaskId,
    cardMenuTaskId,
    menuRef,
    quickUpdateTask,
    editTaskTitle,
    setEditTaskTitle,
    editingTaskId,
    onStartEditTitle,
    saveTaskTitle,
    navigate,
    enumColumns,
}) {
    const shouldShowEmptyEnumColumn = useMemo(() => {
        if (groupBy === 'status' || !allTasks?.edges) return false
        return allTasks.edges.some(({ node }) => {
            const overriddenValue = localOverrides[node.id]
            if (overriddenValue !== undefined) return overriddenValue === null
            const colVal = node.taskcolumnvalueSet?.edges?.find(
                ({ node: columnValueNode }) => String(columnValueNode.column.pk) === String(groupBy)
            )
            return !colVal?.node?.valueString
        })
    }, [allTasks, groupBy, localOverrides])

    const displayColumns = useMemo(() => {
        if (!shouldShowEmptyEnumColumn) return computedColumns
        const groupedColumn = enumColumns.find((column) => String(column.pk) === String(groupBy))
        const emptyColumnLabel = groupedColumn?.title ? `Unassigned ${groupedColumn.title}` : 'Unassigned'
        return [
            { key: EMPTY_ENUM_GROUP_KEY, label: emptyColumnLabel },
            ...computedColumns,
        ]
    }, [computedColumns, enumColumns, groupBy, shouldShowEmptyEnumColumn])

    const getTaskGroupValue = (task) => {
        if (localOverrides[task.id] !== undefined) return localOverrides[task.id]
        if (groupBy === 'status') {
            return task.status?.toUpperCase().replace(/ /g, '_') || 'OPEN'
        }
        const colVal = task.taskcolumnvalueSet?.edges?.find(
            ({ node }) => String(node.column.pk) === String(groupBy)
        )
        return colVal?.node?.valueString || EMPTY_ENUM_GROUP_KEY
    }

    const tasksByColumn = {}
    displayColumns.forEach(col => { tasksByColumn[col.key] = [] })

    if (allTasks?.edges) {
        allTasks.edges.forEach(({ node }) => {
            const val = getTaskGroupValue(node)
            if (val && tasksByColumn[val] !== undefined) {
                tasksByColumn[val].push(node)
            }
        })
    }

    return (
        <>
            {loading && <div className="kanban-loading-bar"><div className="kanban-loading-bar-fill" /></div>}
            <div className={`kanban-board${loading ? ' kanban-board--loading' : ''}`}>
                {displayColumns.map(col => (
                    <div
                        key={col.key}
                        className="kanban-column"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleDrop(col.key)}
                    >
                        <div className="kanban-column-header">
                            <span className={`status-label status-${col.key.toLowerCase()}`}>{col.label}</span>
                            <Badge color="secondary" className="ml-2">{(tasksByColumn[col.key] || []).length}</Badge>
                        </div>
                        <div className="kanban-column-body">
                            {(tasksByColumn[col.key] || []).length === 0 && !loading && (
                                <div className="kanban-empty">No tasks</div>
                            )}
                            {(tasksByColumn[col.key] || []).map(task => (
                                <div
                                    key={task.id}
                                    className={`kanban-card${draggedTaskId === task.id ? ' dragging' : ''}`}
                                    draggable={editingTaskId !== task.id}
                                    onDragStart={() => {
                                        if (editingTaskId === task.id) return
                                        dragJustEndedRef.current = false
                                        setDraggedTaskId(task.id)
                                    }}
                                    onDragEnd={() => {
                                        dragJustEndedRef.current = true
                                        setTimeout(() => { dragJustEndedRef.current = false }, 200)
                                        setDraggedTaskId(null)
                                    }}
                                    onClick={() => {
                                        if (dragJustEndedRef.current || editingTaskId === task.id) return
                                        navigate(`./?task_id=${task.id}`)
                                    }}
                                >
                                    <div className="kanban-card-top">
                                        {editingTaskId === task.id ? (
                                            <input
                                                className="kanban-title-input"
                                                value={editTaskTitle}
                                                autoFocus
                                                onClick={e => e.stopPropagation()}
                                                onChange={e => setEditTaskTitle(e.target.value)}
                                                onBlur={() => saveTaskTitle(task.id)}
                                                onKeyDown={e => {
                                                    e.stopPropagation()
                                                    if (e.key === 'Enter') saveTaskTitle(task.id)
                                                    if (e.key === 'Escape') onStartEditTitle(null, '')
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="kanban-card-title"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    onStartEditTitle(task.id, task.title)
                                                }}
                                                title="Click to edit title"
                                            >
                                                {task.title}
                                            </div>
                                        )}
                                        <div className="kanban-card-menu-btn" ref={cardMenuTaskId === task.id ? menuRef : null} onClick={e => e.stopPropagation()}>
                                            <span className="kanban-card-menu-trigger" onClick={() => setCardMenuTaskId(cardMenuTaskId === task.id ? null : task.id)}>
                                                <i className="fa fa-ellipsis-v" />
                                            </span>
                                            {cardMenuTaskId === task.id && (
                                                <div className="kanban-card-menu-dropdown">
                                                    <div className="kanban-card-menu-section-label">Status</div>
                                                    {KANBAN_COLUMNS.map((statusColumn) => (
                                                        <div
                                                            key={statusColumn.key}
                                                            className={`kanban-card-menu-option${task.status?.toUpperCase() === statusColumn.key ? ' active' : ''}`}
                                                            onClick={() => { quickUpdateTask(task.id, { status: statusColumn.mutationValue }); setCardMenuTaskId(null) }}
                                                        >
                                                            {statusColumn.label}
                                                        </div>
                                                    ))}
                                                    {enumColumns.map((enumCol) => (
                                                        <Fragment key={enumCol.pk}>
                                                            <div className="kanban-card-menu-divider" />
                                                            <div className="kanban-card-menu-section-label">{enumCol.title}</div>
                                                            {(enumCol.allowedOptions || []).map(opt => {
                                                                const currentVal = task.taskcolumnvalueSet?.edges?.find(
                                                                    ({ node }) => String(node.column.pk) === String(enumCol.pk)
                                                                )?.node?.valueString
                                                                return (
                                                                    <div
                                                                        key={opt}
                                                                        className={`kanban-card-menu-option${currentVal === opt ? ' active' : ''}`}
                                                                        onClick={() => { quickUpdateTask(task.id, { columnValues: [{ columnId: enumCol.id, value: opt }] }); setCardMenuTaskId(null) }}
                                                                    >
                                                                        {opt}
                                                                    </div>
                                                                )
                                                            })}
                                                        </Fragment>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="kanban-card-bottom">
                                        <div className="kanban-card-footer">
                                            {task.dueDate && (
                                                <span className="kanban-due-date" style={task.status === "DONE" ? { textDecoration: "line-through" } : {}}>
                                                    <i className="fa fa-calendar" aria-hidden="true" />{' '}
                                                    {moment(task.dueDate).format('MMM D, YYYY')}
                                                </span>
                                            )}
                                            {task.commentsCount > 0 && (
                                                <span className="kanban-comments">
                                                    <i className="fa fa-comments" aria-hidden="true" /> {task.commentsCount}
                                                </span>
                                            )}
                                        </div>
                                        {task.assignedTo?.edges?.length > 0 && (
                                            <div className="kanban-avatars-stack" onClick={e => e.stopPropagation()}>
                                                {task.assignedTo.edges.slice(0, 3).map(({ node: assignee }, idx) => (
                                                    <RepAvatar key={idx} rep={assignee} keepClose />
                                                ))}
                                                {task.assignedTo.edges.length > 3 && (
                                                    <span className="kanban-avatar-overflow">+{task.assignedTo.edges.length - 3}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </>
    )
}

function TaskKanbanView({
    board,
    boardLoading,
    apolloClient,
    boardId,
    groupBy = 'status',
    assignedToIds,
    searchTerm,
    statuses,
    isArchived,
    incidentId,
    orderBy,
    dueDateGte,
    dueDateLte,
    dueDateIsNull,
}) {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const selectedTaskId = searchParams.get('task_id')

    const [draggedTask, setDraggedTask] = useState(null)
    const [dragOverColumn, setDragOverColumn] = useState(null)
    const [localOverrides, setLocalOverrides] = useState({})
    const [cardMenuTaskId, setCardMenuTaskId] = useState(null)
    const [editingTaskId, setEditingTaskId] = useState(null)
    const [editTaskTitle, setEditTaskTitle] = useState("")
    const [taskRegistry, setTaskRegistry] = useState({})
    const [mutationLoadingMap, setMutationLoadingMap] = useState({})
    const menuRef = useRef(null)
    const dragJustEndedRef = useRef(false)

    useEffect(() => {
        if (!cardMenuTaskId) return
        const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setCardMenuTaskId(null) }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [cardMenuTaskId])

    const enumColumns = board?.columnSet?.edges
        ?.filter(({ node }) => node.columnType === 'ENUM' && !node.isArchived)
        .map(({ node }) => node) || []

    const computedColumns = (() => {
        if (groupBy === 'status') return KANBAN_COLUMNS
        const col = enumColumns.find(c => String(c.pk) === String(groupBy))
        if (!col) return KANBAN_COLUMNS
        return (col.allowedOptions || []).map(opt => ({ key: opt, label: opt }))
    })()

    const { data: fallbackTasksData, loading: fallbackTasksLoading } = useQuery(AllTasksQuery, {
        skip: groupBy === 'status',
        variables: {
            boardIds: boardId ? [boardId] : null,
            assignedToIds,
            incidentId,
            searchTerm,
            statuses,
            orderBy,
            isArchived,
            dueDateGte,
            dueDateLte,
            dueDateIsNull,
            first: 100,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-first',
    })

    const onTasksLoaded = useCallback((columnKey, tasks) => {
        setTaskRegistry((prev) => {
            let changed = false
            const next = { ...prev }
            tasks.forEach((task) => {
                if (getComparableTaskSignature(prev[task.id]) !== getComparableTaskSignature(task)) {
                    next[task.id] = task
                    changed = true
                }
            })
            return changed ? next : prev
        })

        setLocalOverrides((prev) => {
            let changed = false
            const next = { ...prev }

            tasks.forEach((task) => {
                if (next[task.id] === columnKey && getStatusColumnKey(task.status) === columnKey) {
                    delete next[task.id]
                    changed = true
                }
            })

            return changed ? next : prev
        })
    }, [])

    const setColumnsLoading = useCallback((columnKeys, delta) => {
        setMutationLoadingMap((prev) => {
            const next = { ...prev }
            columnKeys.forEach((columnKey) => {
                const currentValue = next[columnKey] || 0
                const nextValue = currentValue + delta
                if (nextValue > 0) next[columnKey] = nextValue
                else delete next[columnKey]
            })
            return next
        })
    }, [])

    const handleDrop = useCallback(async (targetColumnKey) => {
        if (!draggedTask) return
        const taskId = draggedTask.id
        const previousColumnKey = getStatusColumnKey(draggedTask.status)
        if (previousColumnKey === targetColumnKey) {
            setDraggedTask(null)
            setDragOverColumn(null)
            return
        }
        if (draggedTask.isAutomated && targetColumnKey === 'DONE') {
            setDraggedTask(null)
            setDragOverColumn(null)
            return
        }

        const targetColumn = KANBAN_COLUMNS.find((column) => column.key === targetColumnKey)
        const targetStatus = targetColumn?.mutationValue || targetColumnKey
        const loadingColumns = [previousColumnKey, targetColumnKey]

        setColumnsLoading(loadingColumns, 1)
        setTaskRegistry((prev) => ({
            ...prev,
            [taskId]: {
                ...draggedTask,
                status: targetStatus,
            },
        }))
        setLocalOverrides(prev => ({ ...prev, [taskId]: targetColumnKey }))
        setDraggedTask(null)
        setDragOverColumn(null)

        try {
            await apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: { taskId, status: targetStatus } },
                refetchQueries: ['AllTasks'],
            })
        } catch (e) {
            setTaskRegistry((prev) => ({
                ...prev,
                [taskId]: draggedTask,
            }))
            setLocalOverrides(prev => {
                const next = { ...prev }
                delete next[taskId]
                return next
            })
        } finally {
            setColumnsLoading(loadingColumns, -1)
        }
    }, [apolloClient, draggedTask, setColumnsLoading])

    const quickUpdateTask = useCallback(async (taskId, values) => {
        try {
            await apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input: { taskId, ...values } },
                refetchQueries: ['AllTasks'],
            })
        } catch (e) { }
    }, [apolloClient])

    const saveTaskTitle = useCallback(async (taskId) => {
        const title = editTaskTitle.trim()
        const original = taskRegistry[taskId]?.title
        if (title && title !== original) {
            await quickUpdateTask(taskId, { title })
        }
        setEditingTaskId(null)
    }, [editTaskTitle, quickUpdateTask, taskRegistry])

    const onStartEditTitle = useCallback((taskId, title) => {
        setEditingTaskId(taskId)
        setEditTaskTitle(title || '')
    }, [])

    return (
        <div className="TaskKanbanView">
            {selectedTaskId && <TaskDetailsSidePanel />}
            {groupBy === 'status' ? (
                <div className="kanban-board">
                    {KANBAN_COLUMNS.map((column) => (
                        <KanbanColumn
                            key={column.key}
                            column={column}
                            boardIds={boardId ? [boardId] : null}
                            assignedToIds={assignedToIds}
                            searchTerm={searchTerm}
                            isArchived={isArchived}
                            incidentId={incidentId}
                            orderBy={orderBy}
                            dueDateGte={dueDateGte}
                            dueDateLte={dueDateLte}
                            dueDateIsNull={dueDateIsNull}
                            localOverrides={localOverrides}
                            taskRegistry={taskRegistry}
                            mutationLoadingMap={mutationLoadingMap}
                            onTasksLoaded={onTasksLoaded}
                            onDrop={handleDrop}
                            dragOverColumn={dragOverColumn}
                            onDragOverColumn={setDragOverColumn}
                            draggedTask={draggedTask}
                            onDragStart={(task) => {
                                setDraggedTask(task)
                                setDragOverColumn(null)
                            }}
                            onDragEnd={() => {
                                setDraggedTask(null)
                                setDragOverColumn(null)
                            }}
                            onCardClick={(taskId) => navigate(`./?task_id=${taskId}`)}
                            onQuickUpdate={quickUpdateTask}
                            onStartEditTitle={onStartEditTitle}
                            editingTaskId={editingTaskId}
                            editTaskTitle={editTaskTitle}
                            setEditTaskTitle={setEditTaskTitle}
                            saveTaskTitle={saveTaskTitle}
                            cardMenuTaskId={cardMenuTaskId}
                            setCardMenuTaskId={setCardMenuTaskId}
                            menuRef={menuRef}
                            dragJustEndedRef={dragJustEndedRef}
                            enumColumns={enumColumns}
                        />
                    ))}
                </div>
            ) : (
                <LegacyKanbanView
                    allTasks={fallbackTasksData?.allTasks}
                    loading={fallbackTasksLoading || boardLoading}
                    computedColumns={computedColumns}
                    localOverrides={localOverrides}
                    groupBy={groupBy}
                    draggedTaskId={draggedTask?.id}
                    dragJustEndedRef={dragJustEndedRef}
                    handleDrop={() => { }}
                    setDraggedTaskId={() => { }}
                    setCardMenuTaskId={setCardMenuTaskId}
                    cardMenuTaskId={cardMenuTaskId}
                    menuRef={menuRef}
                    quickUpdateTask={quickUpdateTask}
                    editTaskTitle={editTaskTitle}
                    setEditTaskTitle={setEditTaskTitle}
                    editingTaskId={editingTaskId}
                    onStartEditTitle={onStartEditTitle}
                    saveTaskTitle={saveTaskTitle}
                    navigate={navigate}
                    enumColumns={enumColumns}
                />
            )}
        </div>
    )
}

export default graphql(BoardQuery, {
    skip: ({ boardId }) => !boardId,
    options: ({ boardId }) => ({
        variables: { id: boardId },
        fetchPolicy: 'cache-first',
    }),
    props: ({ data: { board, loading } }) => ({ board, boardLoading: loading }),
})(withApolloClient(TaskKanbanView))
